import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { format, parseISO } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Clock, LogIn, LogOut, Calendar, Timer, History } from "lucide-react";

const API = "/api";

export default function TimeClock() {
  const { user, getAuthHeader } = useAuth();
  const [status, setStatus] = useState({ is_clocked_in: false, current_record: null });
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dateRange, setDateRange] = useState({
    start: format(new Date(new Date().setDate(1)), "yyyy-MM-dd"),
    end: format(new Date(), "yyyy-MM-dd")
  });

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/time-clock/status`, {
        headers: getAuthHeader()
      });
      setStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch status:", error);
    }
  }, [getAuthHeader]);

  const fetchRecords = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/time-clock/my-records?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { headers: getAuthHeader() }
      );
      setRecords(response.data.records);
    } catch (error) {
      console.error("Failed to fetch records:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, dateRange]);

  useEffect(() => {
    fetchStatus();
    fetchRecords();
  }, [fetchStatus, fetchRecords]);

  // Refresh status when the tab regains focus (handles stale state after sleep / tab switch)
  useEffect(() => {
    const onFocus = () => fetchStatus();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [fetchStatus]);

  const handleClockIn = async () => {
    setClockingIn(true);
    try {
      await axios.post(`${API}/time-clock/clock-in`, {}, { headers: getAuthHeader() });
      toast.success("Clocked in successfully!");
      fetchStatus();
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clock in");
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    setClockingOut(true);
    try {
      const response = await axios.post(`${API}/time-clock/clock-out`, {}, { headers: getAuthHeader() });
      const hours = response?.data?.record?.total_hours;
      toast.success(
        hours != null ? `Clocked out! Total hours: ${hours}` : "Clocked out successfully"
      );
      await fetchStatus();
      await fetchRecords();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      // If server says "Not clocked in", refresh status so UI catches up
      if (detail && /not clocked in/i.test(detail)) {
        await fetchStatus();
        await fetchRecords();
        toast.info("You're already clocked out. Refreshed.");
      } else {
        toast.error(detail || error?.message || "Failed to clock out");
      }
    } finally {
      setClockingOut(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    return format(parseISO(isoString), "hh:mm a");
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    return format(parseISO(isoString), "MMM dd, yyyy");
  };

  // Calculate elapsed time if clocked in
  const getElapsedTime = () => {
    if (!status.current_record?.clock_in) return null;
    const clockIn = new Date(status.current_record.clock_in);
    const diff = Math.floor((currentTime - clockIn) / 1000);
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6" data-testid="time-clock-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-slate-900">Time Clock</h1>
        <p className="text-slate-500">Welcome, {user?.name}</p>
      </div>

      {/* Current Time Card */}
      <Card className="border-slate-200 shadow-sm bg-gradient-to-r from-indigo-50 to-blue-50">
        <CardContent className="pt-6">
          <div className="text-center">
            <p className="text-sm text-slate-500 mb-1">{format(currentTime, "EEEE, MMMM d, yyyy")}</p>
            <p className="text-5xl font-bold text-slate-900 font-mono" data-testid="current-time">
              {format(currentTime, "HH:mm:ss")}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Clock In/Out Card */}
      <Card className={`border-2 shadow-sm ${status.is_clocked_in ? "border-green-200 bg-green-50" : "border-slate-200"}`}>
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            {status.is_clocked_in ? (
              <>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  Currently Working
                </div>
                <div>
                  <p className="text-sm text-slate-500">Clocked in at</p>
                  <p className="text-xl font-semibold text-slate-900">
                    {formatTime(status.current_record?.clock_in)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Time elapsed</p>
                  <p className="text-3xl font-bold text-green-600 font-mono" data-testid="elapsed-time">
                    {getElapsedTime()}
                  </p>
                </div>
                <Button
                  onClick={handleClockOut}
                  disabled={clockingOut}
                  className="w-full max-w-xs bg-red-600 hover:bg-red-700 h-14 text-lg"
                  data-testid="clock-out-btn"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  {clockingOut ? "Clocking Out..." : "Clock Out"}
                </Button>
              </>
            ) : (
              <>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-full">
                  <Clock className="w-4 h-4" />
                  Not Clocked In
                </div>
                <Button
                  onClick={handleClockIn}
                  disabled={clockingIn}
                  className="w-full max-w-xs bg-green-600 hover:bg-green-700 h-14 text-lg"
                  data-testid="clock-in-btn"
                >
                  <LogIn className="w-5 h-5 mr-2" />
                  {clockingIn ? "Clocking In..." : "Clock In"}
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* My Records */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <History className="w-5 h-5 text-slate-500" />
              My Attendance History
            </CardTitle>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="w-36"
                data-testid="start-date"
              />
              <span className="text-slate-400">to</span>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="w-36"
                data-testid="end-date"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Day</TableHead>
                <TableHead className="font-semibold text-center">Clock In</TableHead>
                <TableHead className="font-semibold text-center">Clock Out</TableHead>
                <TableHead className="font-semibold text-right">Hours</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No records found for this period
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record) => (
                  <TableRow key={record.id} data-testid={`record-${record.id}`}>
                    <TableCell className="font-medium">{formatDate(record.clock_in)}</TableCell>
                    <TableCell>{record.clock_in_day}</TableCell>
                    <TableCell className="text-center">{formatTime(record.clock_in)}</TableCell>
                    <TableCell className="text-center">
                      {record.clock_out ? formatTime(record.clock_out) : (
                        <span className="text-green-600 font-medium">Working...</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {record.total_hours ? `${record.total_hours}h` : "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
